import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Printer, FolderOpen, ChevronDown } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import CrudSection from "@/components/CrudSection";
import AttachmentsCell from "@/components/AttachmentsCell";
import NoteCell from "@/components/NoteCell";
import { PageHeader, StatusBadge, fmtDate } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { listResource, updateResource } from "@/lib/api";
import { printRecords } from "@/lib/exportPrint";

const STATUSES = ["on_lot", "in_north_shop", "transferred"];
const SALESPEOPLE = ["Bryan", "Boone", "Graves", "Jeff", "Matt"];
const today = new Date().toISOString().slice(0, 10);

const topFields = [
  { key: "stock_number", label: "Stock #", type: "text", required: true, placeholder: "N-4412" },
  { key: "year", label: "Year", type: "text", required: true, placeholder: "2024" },
  { key: "make", label: "Make", type: "text", required: true, placeholder: "John Deere" },
  { key: "model", label: "Model", type: "text", required: true, placeholder: "6120M" },
  { key: "vin", label: "VIN / Serial #", type: "text", required: true, full: true, placeholder: "1L06120MXXXXXXXX" },
  { key: "usage", label: "Usage (miles / hours)", type: "text", required: true, placeholder: "e.g. 1,240 hrs or 32,000 mi" },
];
const bottomFields = [
  { key: "date_received", label: "Date Received", type: "date", required: true, default: today },
  { key: "cost", label: "Cost", type: "text", required: true, placeholder: "$32,000" },
  { key: "invoice_number", label: "Invoice #", type: "text", required: true, placeholder: "INV-10293" },
  { key: "sale_price_target", label: "List Price", type: "text", required: true, placeholder: "$45,000" },
  { key: "status", label: "Status", type: "select", default: "on_lot", required: true, options: STATUSES },
  { key: "initials", label: "Your Initials", type: "text", required: true, maxLength: 2, placeholder: "e.g. BW" },
  { key: "notes", label: "Description", type: "textarea", full: true, placeholder: "Condition, features, options, wear, anything worth noting" },
];

const newFields = [...topFields, ...bottomFields, { key: "condition", type: "hidden", default: "new" }];
const usedFields = [
  ...topFields,
  { key: "vendor", label: "Previous Owner", type: "text", required: true, full: true, placeholder: "e.g. Miller Farms" },
  ...bottomFields.filter((f) => f.key !== "invoice_number"),
  { key: "condition", type: "hidden", default: "used" },
];

const unitTitle = (r) => [r.year, r.make, r.model].filter(Boolean).join(" ") || "—";

const colStock = { key: "stock_number", label: "Stock #", mono: true, render: (r) => (
  <div>
    <span className="font-bold">{r.stock_number || "—"}</span>
    {r.vin && <div className="text-xs text-gray-500 font-mono-data mt-0.5">{r.vin}</div>}
  </div>
) };
const colUnit = { key: "unit", label: "Unit", render: (r) => <span className="font-medium">{unitTitle(r)}</span> };
const colReceived = { key: "date_received", label: "Received", mono: true, render: (r) => (r.date_received ? fmtDate(r.date_received) : <span className="text-gray-300">—</span>) };
const colUsage = { key: "usage", label: "Usage", render: (r) => r.usage || <span className="text-gray-300">—</span> };
const colCost = { key: "cost", label: "Cost", align: "right", render: (r) => r.cost || <span className="text-gray-300">—</span> };
const colListPrice = { key: "sale_price_target", label: "List Price", align: "right", render: (r) => r.sale_price_target || <span className="text-gray-300">—</span> };
const colStatus = { key: "status", label: "Status", render: (r) => <UnitStatusCell row={r} /> };
const colNotes = { key: "notes", label: "Notes", render: (r) => <NoteCell row={r} path="units" /> };
const colPhotos = { key: "attachments", label: "Photos", render: (r) => (
  <div className="space-y-2">
    <AttachmentsCell row={r} path="units" label="Photos" />
    <div>
      <Link to={`/units/${r.id}`} data-testid="unit-open-folder"
        className="inline-flex items-center gap-1.5 px-2.5 py-1 border-2 border-[#E0A81E] text-[#946a00] text-xs font-bold uppercase tracking-wide hover:bg-[#E0A81E] hover:text-white transition-colors">
        <FolderOpen className="w-3.5 h-3.5" /> Folder
      </Link>
    </div>
  </div>
) };
const colPrevOwner = { key: "vendor", label: "Previous Owner", render: (r) => r.vendor || <span className="text-gray-300">—</span> };

const MarkSold = ({ row }) => {
  const qc = useQueryClient();
  const [price, setPrice] = React.useState(row.sale_price_target || "");
  const [customer, setCustomer] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [salesperson, setSalesperson] = React.useState("Bryan");
  if (row.status === "sold") {
    return <span className="text-xs font-bold uppercase tracking-wide text-gray-400">Sold</span>;
  }
  const sell = async () => {
    if (!customer.trim()) { toast.error("Enter the customer name"); return; }
    if (!salesperson) { toast.error("Pick the salesman"); return; }
    try {
      await updateResource("units", row.id, { status: "sold", sold_price: price, sold_customer: customer.trim(), sold_customer_phone: phone.trim(), sold_salesperson: salesperson });
      qc.invalidateQueries({ queryKey: ["units"] });
      qc.invalidateQueries({ queryKey: ["sales"] });
      toast.success("Moved to Recently Sold");
    } catch {
      toast.error("Couldn't mark sold");
    }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button data-testid="unit-mark-sold"
          className="inline-flex items-center gap-1.5 px-2.5 py-1 border-2 border-[#16265F] text-[#16265F] text-xs font-bold uppercase tracking-wide hover:bg-[#16265F] hover:text-white transition-colors">
          Mark Sold
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent className="rounded-none border-2 border-gray-900">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-head uppercase">Mark {row.stock_number || "unit"} sold?</AlertDialogTitle>
          <AlertDialogDescription>
            This moves the unit into Recently Sold and carries its photos & files over by stock number.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-3 py-1">
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-gray-500">Customer <span className="text-[#D50000]">*</span></label>
            <Input data-testid="unit-sold-customer" value={customer} onChange={(e) => setCustomer(e.target.value)}
              placeholder="Buyer name" className="mt-1 rounded-none border-2 border-gray-900" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-gray-500">Salesman <span className="text-[#D50000]">*</span></label>
            <select data-testid="unit-sold-salesperson" value={salesperson} onChange={(e) => setSalesperson(e.target.value)}
              className="mt-1 w-full rounded-none border-2 border-gray-900 px-3 py-2 text-sm bg-white focus:outline-none">
              {SALESPEOPLE.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-gray-500">Customer Phone</label>
            <Input data-testid="unit-sold-phone" value={phone} onChange={(e) => setPhone(e.target.value)}
              placeholder="712-555-0143" className="mt-1 rounded-none border-2 border-gray-900" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-gray-500">Final Sale Price</label>
            <Input data-testid="unit-sold-price" value={price} onChange={(e) => setPrice(e.target.value)}
              placeholder="$45,000" className="mt-1 rounded-none border-2 border-gray-900" />
          </div>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-none border-2">Cancel</AlertDialogCancel>
          <AlertDialogAction data-testid="unit-mark-sold-confirm" onClick={sell} className="rounded-none bg-[#16265F] hover:bg-[#0f1c47]">Mark Sold</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
const UnitStatusCell = ({ row }) => {
  const qc = useQueryClient();
  const change = async (val) => {
    try {
      await updateResource("units", row.id, { status: val });
      qc.invalidateQueries({ queryKey: ["units"] });
      toast.success("Status updated");
    } catch {
      toast.error("Couldn't update status");
    }
  };
  return (
    <div className="space-y-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="unit-status-quick" className="inline-flex items-center gap-1 group focus:outline-none">
            <StatusBadge status={row.status} />
            <ChevronDown className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-900 transition-colors" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="rounded-none border-2 border-gray-900">
          {STATUSES.map((o) => (
            <DropdownMenuItem key={o} data-testid={`status-option-${o}`} onClick={() => change(o)} className="cursor-pointer focus:bg-gray-100">
              <StatusBadge status={o} />
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <div><MarkSold row={row} /></div>
    </div>
  );
};

const newColumns = [colStock, colUnit, colUsage, colReceived, colCost, colListPrice, colStatus, colNotes, colPhotos];
const usedColumns = [colStock, colUnit, colPrevOwner, colUsage, colReceived, colCost, colListPrice, colStatus, colNotes, colPhotos];

const exportColumns = [
  { label: "Stock #", value: (r) => r.stock_number },
  { label: "Unit", value: (r) => unitTitle(r) },
  { label: "VIN/Serial", value: (r) => r.vin },
  { label: "Usage", value: (r) => r.usage },
  { label: "Condition", value: (r) => r.condition },
  { label: "Make / Prev. Owner", value: (r) => (r.condition === "new" ? r.make : r.vendor) },
  { label: "Received", value: (r) => (r.date_received ? fmtDate(r.date_received) : "") },
  { label: "Cost", value: (r) => r.cost },
  { label: "Invoice #", value: (r) => r.invoice_number },
  { label: "Target", value: (r) => r.sale_price_target },
  { label: "Status", value: (r) => (r.status || "").replace(/_/g, " ") },
  { label: "Notes", value: (r) => r.notes },
];

const isNew = (r) => r.condition === "new";
const tabBtn = "rounded-none border-2 border-gray-900 px-5 py-2 font-bold uppercase tracking-wide text-xs data-[state=active]:bg-gray-900 data-[state=active]:text-white";

const ExportBar = ({ label, records }) => (
  <div className="flex justify-end mb-4">
    <Button data-testid="units-export-btn" variant="outline"
      onClick={() => printRecords({ title: `Units — ${label}`, path: "units", records, columns: exportColumns })}
      className="rounded-none border-2 border-gray-900 font-bold uppercase tracking-wide hover:bg-gray-900 hover:text-white">
      <Printer className="w-4 h-4 mr-2" /> Print / Export
    </Button>
  </div>
);

export default function Units() {
  const { data: units = [] } = useQuery({ queryKey: ["units"], queryFn: () => listResource("units"), refetchInterval: 5000 });
  const newUnits = units.filter(isNew);
  const usedUnits = units.filter((r) => !isNew(r));

  return (
    <div data-testid="units-section">
      <PageHeader title="Units" subtitle="Every machine's folder — intake photos, invoice details and notes, all under one stock number" />
      <Tabs defaultValue="new" className="w-full">
        <TabsList className="mb-6 flex flex-wrap h-auto justify-start gap-2 bg-transparent p-0">
          <TabsTrigger value="new" data-testid="units-tab-new" className={tabBtn}>New{newUnits.length ? ` (${newUnits.length})` : ""}</TabsTrigger>
          <TabsTrigger value="used" data-testid="units-tab-used" className={tabBtn}>Used{usedUnits.length ? ` (${usedUnits.length})` : ""}</TabsTrigger>
        </TabsList>

        <TabsContent value="new" className="mt-0">
          <ExportBar label="New" records={newUnits} />
          <CrudSection title="New Equipment" subtitle="" path="units" testPrefix="units-new"
            addLabel="New Unit Intake" editLabel="Edit Unit" fields={newFields} columns={newColumns} filter={isNew} searchable noDelete />
        </TabsContent>

        <TabsContent value="used" className="mt-0">
          <ExportBar label="Used" records={usedUnits} />
          <CrudSection title="Used Equipment" subtitle="" path="units" testPrefix="units-used"
            addLabel="Used Unit Intake" editLabel="Edit Unit" fields={usedFields} columns={usedColumns} filter={(r) => !isNew(r)} searchable noDelete />
        </TabsContent>
      </Tabs>
    </div>
  );
}
